/**
 * Phase 20.6 — Shuffle Radio Analytics Tracker
 *
 * Passive component (renders nothing). Subscribes to PlayerProvider state
 * and fires /api/shuffle/radio/event whenever a radio queue item is
 * played, skipped (advanced before completion), or completed.
 *
 * Only active when the current track came from the shuffle radio queue —
 * detected by the `_station` field stashed on raw track meta when the
 * user starts radio from ShufflePage.
 */
import { useEffect, useRef } from "react";
import api from "@/lib/api";
import { usePlayer } from "@/components/player/PlayerProvider";

const ShuffleRadioAnalytics = () => {
  const { track, currentTime, duration } = usePlayer();
  // Last-seen state per trackId so we can detect transitions.
  const lastRef = useRef({
    trackId: null,
    item: null,
    lastTime: 0,
    lastDuration: 0,
    playFired: false,
  });

  useEffect(() => {
    const prev = lastRef.current;
    const tid = track?.trackId || null;
    const raw = track?.raw || {};
    const isShuffle = !!raw._station;

    // Detect track change → emit skip/complete for the previous track
    if (prev.trackId && prev.trackId !== tid && prev.item) {
      const prevRaw = prev.item;
      const remaining = Math.max(0, (prev.lastDuration || 0) - (prev.lastTime || 0));
      // "complete" if we were within 5s of the end OR > 80% played
      const wasComplete = prev.lastDuration > 0 &&
        (remaining <= 5 || prev.lastTime / prev.lastDuration >= 0.8);
      const event = wasComplete ? "complete" : "skip";
      api.post("/shuffle/radio/event", {
        queue_id: prevRaw.queue_id || null,
        kind: prevRaw.kind,
        source: prevRaw.source || null,
        item_id: prevRaw.kind === "beat" ? prevRaw.beat_id : prevRaw.submission_id,
        event,
        station: prevRaw._station || "community",
      }).catch(() => {});
    }

    // Fire "play" exactly once per shuffle track
    if (isShuffle && tid && (tid !== prev.trackId || !prev.playFired)) {
      api.post("/shuffle/radio/event", {
        queue_id: raw.queue_id || null,
        kind: raw.kind,
        source: raw.source || null,
        item_id: raw.kind === "beat" ? raw.beat_id : raw.submission_id,
        event: "play",
        station: raw._station || "community",
      }).catch(() => {});
      lastRef.current = {
        trackId: tid,
        item: raw,
        lastTime: 0,
        lastDuration: 0,
        playFired: true,
      };
      return;
    }

    if (tid !== prev.trackId) {
      lastRef.current = {
        trackId: tid,
        item: isShuffle ? raw : null,
        lastTime: 0,
        lastDuration: 0,
        playFired: false,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track?.trackId]);

  // Track time/duration so the transition handler has fresh values.
  useEffect(() => {
    lastRef.current = {
      ...lastRef.current,
      lastTime: currentTime || 0,
      lastDuration: duration || lastRef.current.lastDuration,
    };
  }, [currentTime, duration]);

  return null;
};

export default ShuffleRadioAnalytics;
